import React, { useState } from 'react';
import { Box, Text, Flex, Badge } from '@chakra-ui/react';
import { Accordion, ButtonText, Modal } from '@morressier/alembic';
import {
  IRecommendationParticipant,
  IRecommendationRound
} from '@publishing-engine/domain/models';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile, faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import RecommendationBadge from 'src/components/RecommendationBadge';
import { ModerateEICComment } from './ModerateEICComment';
import { CommentDiffModal } from './CommentDiffModal';
import FileViewerButtonText from '../FileViewerButtonText';

const renderItem = (
  RecommendationParticipant: IRecommendationParticipant,
  submissionContextId?: string
) => {
  const {
    commentForAuthor = '',
    attachments = [],
    decision,
    moderatedCommentForAuthor,
    moderatedAttachments = []
  } = RecommendationParticipant?.recommendation ?? {};
  const [isOpen, setIsOpen] = useState(false);
  const [showUpdate, setShowUpdate] = useState(false);
  const onOpen = () => setIsOpen(true);
  const onClose = () => setIsOpen(false);
  const displayAttachments =
    moderatedAttachments.length > 0 ? moderatedAttachments : attachments;
  const showAttachments = (displayAttachments ?? []).filter(
    file => file?.visibility === 'show'
  );

  const hasCommentUpdate =
    moderatedCommentForAuthor && moderatedCommentForAuthor !== commentForAuthor;

  const hasAttachmentUpdate =
    (moderatedAttachments?.length ?? 0) > (attachments?.length ?? 0);

  const hasVisibilityChange = (attachments ?? []).some(original => {
    const moderated = (moderatedAttachments ?? []).find(
      file => file?.referenceName === original?.referenceName
    );
    return moderated && moderated.visibility !== original.visibility;
  });

  return (
    <>
      <Flex flexDirection="column" gap={6} padding={2}>
        <Flex justifyContent={'space-between'}>
          <Box>
            {decision && (
              <>
                <RecommendationBadge recommendation={decision} />{' '}
                <ButtonText onClick={onOpen}>
                  <FontAwesomeIcon icon={faPenToSquare} />
                </ButtonText>
              </>
            )}
          </Box>
          {(hasCommentUpdate || hasAttachmentUpdate || hasVisibilityChange) && (
            <Flex textStyle="textBody" gap={2} alignItems="center">
              <Badge variant="attention" size="context">
                <Flex gap={1} alignItems="center">
                  <FontAwesomeIcon icon={faPenToSquare} />
                  Moderated
                </Flex>
              </Badge>{' '}
              <ButtonText
                onClick={() => {
                  setShowUpdate(true);
                }}
              >
                View Updates
              </ButtonText>
            </Flex>
          )}
        </Flex>
        {(commentForAuthor || moderatedCommentForAuthor) && (
          <Box>
            <Text textStyle="headingItem">Feedback for Authors </Text>
            <Text textStyle="textBody">
              {moderatedCommentForAuthor || commentForAuthor}
            </Text>
          </Box>
        )}

        {!!showAttachments?.length && (
          <Box marginTop={2}>
            <Text textStyle="headingItem"> Attachments</Text>
            <Flex flexDirection="column" marginTop={2} gap={2}>
              {showAttachments.map(attachment => (
                <Flex
                  justifyContent="space-between"
                  key={attachment?.referenceName}
                >
                  <Flex gap={2} alignItems="center">
                    <FontAwesomeIcon icon={faFile} />
                    <Text textStyle="textBody">
                      {attachment?.referenceName}
                    </Text>
                    <FileViewerButtonText
                      file={attachment}
                      submissionContextId={submissionContextId}
                      textStyle="textBody"
                      flexShrink={0}
                    >
                      View
                    </FileViewerButtonText>
                  </Flex>
                </Flex>
              ))}
            </Flex>
          </Box>
        )}
      </Flex>
      {isOpen && (
        <Modal isOpen={isOpen} onClose={() => onClose()} width="45rem">
          <ModerateEICComment
            onClose={onClose}
            Recommendation={RecommendationParticipant?.recommendation}
            Role={RecommendationParticipant?.role}
            UserId={RecommendationParticipant?.userId}
          />
        </Modal>
      )}
      {showUpdate && (
        <CommentDiffModal
          Recommendation={RecommendationParticipant?.recommendation}
          submissionContextId={submissionContextId}
          onClose={() => {
            setShowUpdate(false);
          }}
        />
      )}
    </>
  );
};

const ModerateCOCComment: React.FC<{
  RecommendationRound: IRecommendationRound[];
  submissionContextId?: string;
}> = ({ RecommendationRound, submissionContextId }) => {
  const latestRecommendationCycle = RecommendationRound?.at(-1)?.cycle?.at(-1);
  return latestRecommendationCycle?.participants
    ?.filter(item => item?.recommendation)
    .sort((a, b) => a?.role?.length - b?.role?.length)
    .map(participant => {
      return (
        <Accordion
          accordionItemProps={{
            marginBottom: 0,
            borderTop: '1px',
            borderColor: 'neutralPrimaryBorder',
            _first: {
              borderColor: 'none',
              borderTop: '0'
            }
          }}
          items={[participant].map(commentdata => ({
            title: `${commentdata?.role} comment`,
            content: renderItem(commentdata, submissionContextId)
          }))}
        />
      );
    });
};

export default ModerateCOCComment;
